import { Fragment, memo, useEffect, useRef, useState, type ReactNode } from "react";
import { Check, Copy, GitPullRequest, GripVertical, Maximize2, Zap } from "lucide-react";

import { DependencyPicker } from "@/presentation/components/board/dependency-picker";
import { DomainPill } from "@/presentation/components/roadmaps/domain-pill";
import { StatusDot } from "@/presentation/components/status/status-dot";
import {
  DELIVERY_STATUS_LABEL,
  DELIVERY_STATUS_ORDER,
  DELIVERY_STATUS_TONE,
  IMPL_KIND_LABEL,
  type BoardColumn,
} from "@/presentation/data/board";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@monorepo-apps/ui/src/components/select";
import { cn } from "@monorepo-apps/ui/src/lib/utils";
import type {
  BoardFeatureRow as BoardFeature,
  BoardImplKind,
  MilestoneRow,
  MvpComponentRow,
  StakeholderRow,
  WorkstreamRow,
} from "@/infrastructure/supabase/database.types";

const NONE = "__none__";
const IMPL_KINDS: BoardImplKind[] = ["native", "build", "scratch", "mixed"];
const CELL = "border-border/60 flex items-center border-l px-2";
const TRIGGER =
  "font-data h-8 w-full justify-between gap-1 border-transparent bg-transparent px-2 text-[12px] hover:border-border hover:bg-muted/40";
const INTERACTIVE = "input,textarea,button,[role=combobox]";

/**
 * Performance: the editable cells are lazy. By default a cell renders a cheap
 * display button; the Radix Select — and crucially its option list, built by
 * `renderOptions()` — is only mounted when the cell is clicked. This keeps the
 * board's initial mount from constructing ~690 Selects × their option lists for
 * 138 rows up front (the cause of the multi-second navigation stall).
 */
function LazySelect({
  display,
  ariaLabel,
  value,
  onValueChange,
  renderOptions,
  readOnly = false,
}: {
  display: ReactNode;
  ariaLabel: string;
  value: string;
  onValueChange: (v: string) => void;
  renderOptions: () => ReactNode;
  readOnly?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  // View-only: render the value, not a control. handlePatch already drops the
  // write and RLS would reject it anyway, so an interactive cell here would open
  // a picker, accept a choice, and silently do nothing.
  if (readOnly) {
    return (
      <span className={cn(TRIGGER, "flex items-center")} aria-label={ariaLabel}>
        {display}
      </span>
    );
  }
  if (!editing) {
    return (
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        className={cn(TRIGGER, "flex items-center")}
      >
        {display}
      </button>
    );
  }
  return (
    <Select
      defaultOpen
      value={value}
      onValueChange={(v) => {
        onValueChange(v);
        setEditing(false);
      }}
      onOpenChange={(o) => {
        if (!o) setEditing(false);
      }}
    >
      <SelectTrigger className={TRIGGER} aria-label={ariaLabel}>
        {display}
      </SelectTrigger>
      <SelectContent>{renderOptions()}</SelectContent>
    </Select>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const TODAY_MS = (() => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
})();
function fmtDue(iso: string): string {
  const d = new Date(iso + "T00:00:00");
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** Lazy date cell — cheap display until clicked, then a native date input. */
function LazyDate({
  value,
  display,
  onChange,
  readOnly = false,
}: {
  value: string | null;
  display: ReactNode;
  onChange: (v: string | null) => void;
  readOnly?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  if (readOnly) {
    return (
      <span className={cn(TRIGGER, "flex items-center")} aria-label="Due date">
        {display}
      </span>
    );
  }
  if (!editing) {
    return (
      <button
        type="button"
        aria-label="Due date"
        onClick={(e) => {
          e.stopPropagation();
          setEditing(true);
        }}
        className={cn(TRIGGER, "flex items-center")}
      >
        {display}
      </button>
    );
  }
  return (
    <input
      type="date"
      autoFocus
      defaultValue={value ?? ""}
      onClick={(e) => e.stopPropagation()}
      onBlur={(e) => {
        const next = e.target.value || null;
        if (next !== value) onChange(next);
        setEditing(false);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        else if (e.key === "Escape") setEditing(false);
      }}
      className="border-border bg-background font-data h-8 w-full rounded-sm border px-2 text-[12px] outline-none"
    />
  );
}

interface BoardFeatureRowProps {
  feature: BoardFeature;
  orderedColumns: BoardColumn[];
  gridTemplate: string;
  components: MvpComponentRow[];
  componentsById: Map<string, MvpComponentRow>;
  milestones: MilestoneRow[];
  stakeholders: StakeholderRow[];
  workstreams: WorkstreamRow[];
  workstreamCodeByKey: Map<string, string>;
  isDragging: boolean;
  /** view-only: every cell renders its value instead of an editor */
  readOnly?: boolean;
  // Stable, id-based handlers (the row supplies its own feature.id) so the row
  // can be memoized — see React.memo at the bottom.
  onPatch: (id: string, patch: Partial<BoardFeature>) => void;
  onOpen: (id: string) => void;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDropRelative: (id: string, position: "before" | "after") => void;
}

function BoardFeatureRowImpl({
  feature,
  orderedColumns,
  gridTemplate,
  components,
  componentsById,
  milestones,
  stakeholders,
  workstreams,
  workstreamCodeByKey,
  isDragging,
  readOnly = false,
  onPatch,
  onOpen,
  onDragStart,
  onDragEnd,
  onDropRelative,
}: BoardFeatureRowProps) {
  const [dropEdge, setDropEdge] = useState<"before" | "after" | null>(null);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
    },
    []
  );
  const applyPatch = (patch: Partial<BoardFeature>) => onPatch(feature.id, patch);

  const component = feature.component_id ? componentsById.get(feature.component_id) : undefined;
  const milestone = feature.milestone_key
    ? milestones.find((m) => m.key === feature.milestone_key)
    : undefined;
  const owner = feature.owner_id ? stakeholders.find((s) => s.id === feature.owner_id) : undefined;
  const workstream = workstreams.find((w) => w.key === feature.workstream_key);

  const cells: Record<string, ReactNode> = {
    // Changing this moves the item into that block. When the board is grouped by
    // workstream (the default) the row leaves this group and reappears under the
    // target one — the page appends it to the end of the destination.
    block: (
      <div className={CELL}>
        <LazySelect
          readOnly={readOnly}
          ariaLabel="Block"
          value={feature.workstream_key}
          onValueChange={(v) => {
            if (v !== feature.workstream_key) applyPatch({ workstream_key: v });
          }}
          display={
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="font-data text-muted-foreground shrink-0 text-[10px] tracking-tight">
                {workstream ? workstream.code : "?"}
              </span>
              <span className={cn("truncate", !workstream && "text-muted-foreground")}>
                {workstream ? workstream.title : feature.workstream_key}
              </span>
            </span>
          }
          renderOptions={() =>
            workstreams.map((w) => (
              <SelectItem key={w.key} value={w.key} className="text-[13px]">
                <span className="flex items-center gap-2">
                  <span className="font-data text-muted-foreground text-[10px]">{w.code}</span>
                  {w.title}
                </span>
              </SelectItem>
            ))
          }
        />
      </div>
    ),
    status: (
      <div className={CELL}>
        <LazySelect
          readOnly={readOnly}
          ariaLabel="Status"
          value={feature.delivery_status}
          onValueChange={(v) =>
            applyPatch({ delivery_status: v as BoardFeature["delivery_status"] })
          }
          display={
            <span className="flex min-w-0 items-center gap-2">
              <StatusDot tone={DELIVERY_STATUS_TONE[feature.delivery_status]} size="md" />
              <span className="truncate">{DELIVERY_STATUS_LABEL[feature.delivery_status]}</span>
            </span>
          }
          renderOptions={() =>
            DELIVERY_STATUS_ORDER.map((s) => (
              <SelectItem key={s} value={s} className="text-[13px]">
                <span className="flex items-center gap-2">
                  <StatusDot tone={DELIVERY_STATUS_TONE[s]} size="md" />
                  {DELIVERY_STATUS_LABEL[s]}
                </span>
              </SelectItem>
            ))
          }
        />
      </div>
    ),
    owner: (
      <div className={CELL}>
        <LazySelect
          readOnly={readOnly}
          ariaLabel="Owner"
          value={feature.owner_id ?? NONE}
          onValueChange={(v) => applyPatch({ owner_id: v === NONE ? null : v })}
          display={
            <span className={cn("truncate", !owner && "text-muted-foreground")}>
              {owner ? owner.name : "Unassigned"}
            </span>
          }
          renderOptions={() => (
            <>
              <SelectItem value={NONE} className="text-[13px]">
                Unassigned
              </SelectItem>
              {stakeholders.map((s) => (
                <SelectItem key={s.id} value={s.id} className="text-[13px]">
                  {s.name}
                </SelectItem>
              ))}
            </>
          )}
        />
      </div>
    ),
    milestone: (
      <div className={CELL}>
        <LazySelect
          readOnly={readOnly}
          ariaLabel="Milestone"
          value={feature.milestone_key ?? NONE}
          onValueChange={(v) => applyPatch({ milestone_key: v === NONE ? null : v })}
          display={
            <span className={cn("truncate", !milestone && "text-muted-foreground")}>
              {milestone ? milestone.code : "—"}
            </span>
          }
          renderOptions={() => (
            <>
              <SelectItem value={NONE} className="text-[13px]">
                None
              </SelectItem>
              {milestones.map((m) => (
                <SelectItem key={m.key} value={m.key} className="text-[13px]">
                  {m.code} — {m.title}
                </SelectItem>
              ))}
            </>
          )}
        />
      </div>
    ),
    due: (
      <div className={CELL}>
        <LazyDate
          readOnly={readOnly}
          value={feature.due_date}
          onChange={(v) => applyPatch({ due_date: v })}
          display={(() => {
            const overdue =
              feature.due_date &&
              feature.delivery_status !== "done" &&
              new Date(feature.due_date + "T00:00:00").getTime() < TODAY_MS;
            return (
              <span
                className={cn(
                  "truncate",
                  !feature.due_date && "text-muted-foreground",
                  overdue && "text-[var(--rag-red)]"
                )}
              >
                {feature.due_date ? fmtDue(feature.due_date) : "—"}
              </span>
            );
          })()}
        />
      </div>
    ),
    component: (
      <div className={CELL}>
        <LazySelect
          readOnly={readOnly}
          ariaLabel="Component"
          value={feature.component_id ?? NONE}
          onValueChange={(v) => applyPatch({ component_id: v === NONE ? null : v })}
          display={
            <span className={cn("truncate", !component && "text-muted-foreground")}>
              {component ? component.name_en : "—"}
            </span>
          }
          renderOptions={() => (
            <>
              <SelectItem value={NONE} className="text-[13px]">
                None
              </SelectItem>
              {components.map((c) => (
                <SelectItem key={c.id} value={c.id} className="text-[13px]">
                  {c.name_en}
                </SelectItem>
              ))}
            </>
          )}
        />
      </div>
    ),
    impl: (
      <div className={CELL}>
        <LazySelect
          readOnly={readOnly}
          ariaLabel="Implementation"
          value={feature.impl_kind ?? NONE}
          onValueChange={(v) => applyPatch({ impl_kind: v === NONE ? null : (v as BoardImplKind) })}
          display={
            <span className={cn("truncate", !feature.impl_kind && "text-muted-foreground")}>
              {feature.impl_kind ? IMPL_KIND_LABEL[feature.impl_kind] : "—"}
            </span>
          }
          renderOptions={() => (
            <>
              <SelectItem value={NONE} className="text-[13px]">
                —
              </SelectItem>
              {IMPL_KINDS.map((k) => (
                <SelectItem key={k} value={k} className="text-[13px]">
                  {IMPL_KIND_LABEL[k]}
                </SelectItem>
              ))}
            </>
          )}
        />
      </div>
    ),
    domain: (
      <div className={cn(CELL, "px-3")}>
        {component?.domain ? (
          <DomainPill domain={component.domain} />
        ) : (
          <span className="text-muted-foreground text-[12px]">—</span>
        )}
      </div>
    ),
    deps: (
      <div className={cn(CELL, "justify-center")}>
        <DependencyPicker
          variant="popover"
          readOnly={readOnly}
          feature={feature}
          componentsById={componentsById}
          workstreamCodeByKey={workstreamCodeByKey}
        />
      </div>
    ),
    critical: (
      <div className={cn(CELL, "justify-center")}>
        <button
          type="button"
          disabled={readOnly}
          onClick={() => applyPatch({ mvp_blocking: !feature.mvp_blocking })}
          aria-pressed={feature.mvp_blocking}
          aria-label="Toggle critical path"
          title={feature.mvp_blocking ? "Critical path — click to clear" : "Mark critical path"}
          className={cn(
            "flex size-7 items-center justify-center rounded-sm transition",
            feature.mvp_blocking
              ? "text-[var(--rag-red)]"
              : "text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted"
          )}
        >
          <Zap className={cn("size-3.5", feature.mvp_blocking && "fill-current")} />
        </button>
      </div>
    ),
    pr: (
      <div className={cn(CELL, "justify-center")}>
        {feature.pr_url ? (
          <a
            href={feature.pr_url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={feature.pr_url}
            className="text-primary inline-flex items-center gap-1 text-[12px] hover:underline"
          >
            <GitPullRequest className="size-3 shrink-0" />
            {feature.pr_number ? `#${feature.pr_number}` : "PR"}
          </a>
        ) : (
          <span className="text-muted-foreground text-[12px]">—</span>
        )}
      </div>
    ),
  };

  return (
    <div
      draggable
      onDragStart={(e) => {
        if ((e.target as HTMLElement).closest(INTERACTIVE)) {
          e.preventDefault();
          return;
        }
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", feature.id);
        onDragStart(feature.id);
      }}
      onDragEnd={() => {
        setDropEdge(null);
        onDragEnd();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        const r = e.currentTarget.getBoundingClientRect();
        setDropEdge(e.clientY < r.top + r.height / 2 ? "before" : "after");
      }}
      onDragLeave={() => setDropEdge(null)}
      onDrop={(e) => {
        e.preventDefault();
        const edge = dropEdge ?? "before";
        setDropEdge(null);
        onDropRelative(feature.id, edge);
      }}
      className={cn(
        "group border-border relative grid w-full items-stretch border-t transition-colors select-none hover:bg-[oklch(0.55_0.17_190_/_0.03)]",
        isDragging && "opacity-40"
      )}
      style={{ gridTemplateColumns: gridTemplate }}
    >
      {dropEdge ? (
        <div
          className={cn(
            "bg-primary pointer-events-none absolute right-0 left-0 z-[3] h-0.5",
            dropEdge === "before" ? "-top-px" : "-bottom-px"
          )}
          aria-hidden
        />
      ) : null}

      {/* Item — sticky first column */}
      <div className="bg-card group-hover:bg-card sticky left-0 z-[1] flex items-center gap-1 py-2 pr-2 pl-2 shadow-[1px_0_0_var(--border)]">
        <GripVertical
          className="text-muted-foreground/40 group-hover:text-muted-foreground size-4 shrink-0 cursor-grab"
          aria-hidden
        />
        {/* Fixed-width code handle (tap to copy) — kept left of the critical slot so
            codes and titles stay aligned whether or not a row is critical. The
            dotted underline + copy glyph signal it's tap-to-copy. */}
        <button
          type="button"
          aria-label={copied ? "Copied" : `Copy code ${feature.code ?? ""}`}
          title={copied ? "Copied!" : "Tap to copy code"}
          onClick={(e) => {
            e.stopPropagation();
            if (!feature.code) return;
            navigator.clipboard
              ?.writeText(feature.code)
              .then(() => {
                setCopied(true);
                if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
                copyTimer.current = window.setTimeout(() => setCopied(false), 1200);
              })
              .catch(() => {});
          }}
          className={cn(
            "font-data group/code hover:bg-muted flex w-16 shrink-0 cursor-pointer items-center gap-0.5 rounded-[4px] px-1 py-0.5 text-[10px] tabular-nums transition-colors",
            copied ? "text-primary" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <span
            className={cn(
              "truncate underline decoration-dotted underline-offset-2",
              copied
                ? "decoration-transparent"
                : "decoration-muted-foreground/40 group-hover/code:decoration-current"
            )}
          >
            {copied ? "copied" : (feature.code ?? "")}
          </span>
          {feature.code ? (
            copied ? (
              <Check className="size-2.5 shrink-0" aria-hidden />
            ) : (
              <Copy
                className="size-2.5 shrink-0 opacity-50 transition-opacity group-hover/code:opacity-100"
                aria-hidden
              />
            )
          ) : null}
        </button>
        <span className="flex w-3 shrink-0 items-center justify-center" aria-hidden>
          {feature.mvp_blocking ? (
            <Zap
              className="size-3 text-[var(--rag-red)]"
              aria-label="MVP-blocking / critical path"
            />
          ) : null}
        </span>
        <div className="flex min-w-0 flex-1 flex-col">
          <input
            key={feature.id + feature.title}
            type="text"
            defaultValue={feature.title}
            aria-label="Feature title"
            readOnly={readOnly}
            className="focus:border-ring focus:bg-background hover:border-border/60 truncate rounded-sm border border-transparent bg-transparent px-1 py-0.5 text-[13px] font-medium outline-none"
            onClick={(e) => e.stopPropagation()}
            onBlur={(e) => {
              const next = e.target.value.trim();
              if (next && next !== feature.title) applyPatch({ title: next });
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur();
              else if (e.key === "Escape") {
                e.currentTarget.value = feature.title;
                e.currentTarget.blur();
              }
            }}
          />
          {component ? (
            <span className="font-data text-muted-foreground truncate px-1 text-[10px] tracking-tight">
              {component.name_en}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => onOpen(feature.id)}
          aria-label="Open feature detail"
          title="Open detail"
          className="text-muted-foreground hover:bg-muted hover:text-foreground flex size-7 shrink-0 items-center justify-center rounded-sm opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100"
        >
          <Maximize2 className="size-3.5" />
        </button>
      </div>

      {orderedColumns.map((c) => (
        <Fragment key={c.key}>{cells[c.key]}</Fragment>
      ))}
    </div>
  );
}

export const BoardFeatureRow = memo(BoardFeatureRowImpl);
